import { globalEvent } from '@shared/easyfy.core';
import { show, hide, addCss, removeCss } from '@shared/easyfy.utils';
import { invoke } from '@shared/easyfy.decorators';
import { animationEnded } from '@clientShared/utils';
import basketService from '@webServices/basket.service';
import {
  $favourites,
  $favouriteIds,
  $favouriteSkus,
  $favouriteCount,
  addFavourite,
  removeFavourite,
  updateSalesUnit,
  fetchFlyoutHtml,
  syncWithServer,
  initialSyncComplete,
} from '@orderTemplatesServices/favourites.store';
import MessageService from '@clientWebServices/message.service';
import Toggle from '@clientShared/toggle';
import Basket from '@clientWebModules/basket';
import { logCustomMessage } from '@shared/easyfy.logger';
import EasyfyModules from '@shared/easyfy.events/easyfy.eventhandler';
import { HayppEventNames } from '@clientWebModules/haypp-event-names';

interface SalesUnitInfo {
  salesUnitCode: string;
  salesUnit: number;
}

@invoke
export default class Favourites {
  static Instance: Favourites;

  constructor() {
    const headerFavourites = document.querySelector('[data-container="header-favourites"]');
    if (!(headerFavourites instanceof HTMLElement)) return;

    document.addEventListener('form-success', (e: Event) => {
      if ((e.target as HTMLElement)?.dataset?.container === 'login-form') {
        syncWithServer().catch((error) => {
          logCustomMessage('Error in eventlistener for login form', {
            action: 'favourites constructor',
            errorMessage: error instanceof Error ? error.message : String(error),
            errorStack: error instanceof Error ? (error.stack ?? '') : '',
          });
        });
      }
    });

    // subscribe() fires immediately with current value, replacing the old initSavedState() call.
    $favouriteIds.subscribe((ids) => this.updateFavouriteButtonsState(document, ids));

    $favouriteCount.subscribe((count) => this.updateCountBadges(count));

    // Push the saved-product SKUs to the dataLayer once the page-load sync has settled,
    // so authenticated cross-device favourites are included.
    initialSyncComplete.then(() => this.pushSavedProductsOnLoad()).catch(() => {});

    globalEvent({
      type: 'click',
      events: [
        {
          action: 'toggle-favourite',
          callback: this.onToggleFavourite,
        },
        {
          action: 'toggle-favourites',
          callback: this.toggleFavouritesFlyout,
        },
        {
          action: 'favourites-all-cart',
          callback: this.onAddAllToCart,
        },
      ],
    });
    globalEvent({
      type: 'change',
      events: [{ event: 'sales-unit-select', callback: this.onSalesUnitChange }],
    });
  }

  private pushSavedProductsOnLoad() {
    const skus = [...new Set($favouriteSkus.get())].join(',');
    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer ??= [];
    w.dataLayer.push({ cd_saved_products: skus });
  }

  private updateFavouriteButtonsState(root: ParentNode, ids: readonly string[]) {
    root.querySelectorAll<HTMLElement>('[data-action="toggle-favourite"]').forEach((btn) => {
      const isFavourite = ids.includes(btn.dataset.productid ?? '');
      btn.classList.toggle('is-favourite', isFavourite);
      btn.setAttribute('aria-pressed', `${isFavourite}`);
    });
  }

  private updateCountBadges(count: number) {
    document.querySelectorAll<HTMLElement>('[data-container="header-favourites"]').forEach((button) => {
      const label = count > 0 ? button.dataset.ariaLabelCount : button.dataset.ariaLabelEmpty;
      if (label) button.setAttribute('aria-label', label.replace('{0}', `${count}`));
    });

    document.querySelectorAll<HTMLElement>('[data-container="header-favourites"] [data-container="favourites-badge"]').forEach((badge) => {
      badge.textContent = count > 0 ? `${count}` : '';

      if (count > 0) {
        show(badge, 'flex');
      } else {
        hide(badge);
      }
    });
  }

  private updateFavouritesAnnouncer(count: number) {
    const announcer = document.querySelector<HTMLElement>('[data-container="favourites-announcer"]');
    const text = announcer?.dataset.text;
    if (!announcer || !text) return;

    announcer.textContent = text.replace('{0}', `${count}`);
  }

  private getSalesUnitInfo(element: HTMLElement): SalesUnitInfo | null {
    const container = element.closest<HTMLElement>('[data-container="product-card"]') ?? element.closest<HTMLElement>('[data-container="product"]');
    const selectedOption = container?.querySelector<HTMLElement>('[data-container="salesunit-option"][data-selected]');
    if (!selectedOption) return null;

    const salesUnitCode = selectedOption.dataset.salesunitcode;
    const salesUnit = parseInt(selectedOption.querySelector<HTMLElement>('[data-container="packsize"]')?.dataset.packsize ?? '', 10);

    if (!salesUnitCode || isNaN(salesUnit)) return null;
    return { salesUnitCode, salesUnit };
  }

  private updateAddAllToCartButtonState(flyout: HTMLElement): void {
    const button = flyout.querySelector<HTMLElement>('[data-action="favourites-all-cart"]');
    if (!button) return;
    const productCards = flyout.querySelectorAll('[data-container="product-card"]');
    const disabled = productCards.length === 0 || Array.from(productCards).some((card) => !card.querySelector('[data-action="add-to-cart"]'));
    disabled ? addCss(button, 'hidden') : removeCss(button, 'hidden');
  }

  // No longer async - mutations are synchronous; HTTP fires inside the store's listen handler.
  onToggleFavourite = (e: Event) => {
    const target = e.target;
    if (!(target instanceof HTMLElement)) return;

    const element = target.closest<HTMLElement>('[data-action="toggle-favourite"]');
    if (!element) return;

    const productId = element.dataset?.productid ?? '';
    const sku = element.dataset?.sku;
    const productName = element.dataset?.productName;
    const productListName = element.dataset?.originatedFromListName || element?.closest<HTMLElement>('[data-list-name]')?.dataset.listName;
    const isFavourite = $favouriteIds.get().includes(productId);
    const hayppEventName = isFavourite ? HayppEventNames.SaveProductRemove : HayppEventNames.SaveProductAdd;

    const salesUnitInfo = this.getSalesUnitInfo(element);
    const salesUnit = salesUnitInfo ? { unitCode: salesUnitInfo.salesUnitCode, unitPackSize: salesUnitInfo.salesUnit } : null;

    if (isFavourite) {
      removeFavourite(productId);
    } else {
      addFavourite(productId, sku, salesUnit);
    }

    this.updateFavouritesAnnouncer($favouriteCount.get());

    EasyfyModules.EasyfyEventHandler.RunEvent({
      eventName: hayppEventName,
      data: { productIdentifier: sku, productName, listName: productListName },
    }).catch((error) => {
      logCustomMessage(`Failed to send saved product ${hayppEventName} event`, {
        action: 'onToggleFavourite',
        productId,
        sku: sku ?? '',
        productName: productName ?? '',
        listName: productListName ?? '',
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? (error.stack ?? '') : '',
      });
    });

    // DOM: remove the card from an open flyout or MyPages list when un-saving.
    // Badge and is-favourite updates flow from subscriptions above - no manual DOM scan needed.
    if (isFavourite) {
      const favouriteLists = document.querySelectorAll<HTMLElement>('[data-favourite-list]');
      favouriteLists.forEach((favouriteList) => {
        const productCardAction = favouriteList.querySelector(`[data-action="toggle-favourite"][data-productid="${productId}"]`);
        const productCard = productCardAction?.closest<HTMLElement>('[data-container="product-card"]');
        if (productCard && favouriteList.contains(productCard)) {
          productCard.remove();
          this.updateAddAllToCartButtonState(favouriteList);
        }
      });
    }
  };

  onAddAllToCart = (e: Event) => {
    const element = (e.target as HTMLElement).closest<HTMLElement>('[data-action="favourites-all-cart"]');
    if (!element) return;

    const token = element.closest<HTMLElement>('[data-token]')?.dataset.token ?? '';
    const products = $favourites.get();
    if (products.length === 0) return;

    const basket: basketService.IBasket = {
      rows: products.map((p) => ({
        productId: p.productId,
        salesUnitCode: p.salesUnit?.unitCode,
        changeQty: 1,
      })),
    };

    basketService
      .UpdateBasket(basket, token)
      .then((result) => {
        if (result.data?.success) {
          Basket.Instance.renderBasket(result.data.content, false);

          Toggle.Instance.closeOpenModals();

          const headerBasket: HTMLElement = document.querySelector('[data-container="header-basket"]') as HTMLElement;
          addCss(headerBasket, 'bounce');
          animationEnded(headerBasket, () => {
            removeCss(headerBasket, 'bounce');
          });
        } else {
          throw new Error('Failed to add all to cart from favourites');
        }
      })
      .catch((error) => {
        logCustomMessage('Error when adding all to cart from favourites', {
          action: 'onAddAllToCart',
          errorMessage: error instanceof Error ? error.message : String(error),
          errorStack: error instanceof Error ? (error.stack ?? '') : '',
        });
        MessageService.Instance.displayDefaultErrorMessage();
      });
  };

  onSalesUnitChange = (e: Event) => {
    const element = e.target;
    if (!(element instanceof HTMLElement)) return;
    if (!element.closest('[data-container="favourites-flyout"]')) return;

    const label = element.closest<HTMLElement>('label[data-container="salesunit-option"]');
    if (!label) return;

    const productId = label.dataset.value ?? '';
    const salesUnitCode = label.dataset.salesunitcode ?? '';
    const packsize = parseInt(label.querySelector<HTMLElement>('[data-container="packsize"]')?.dataset.packsize ?? '', 10);

    if (!productId || !salesUnitCode || isNaN(packsize)) return;

    updateSalesUnit(productId, { unitCode: salesUnitCode, unitPackSize: packsize });
  };

  toggleFavouritesFlyout = async (e: Event) => {
    const target = (e.target as HTMLElement).closest<HTMLButtonElement>('button');
    if (!target) return;

    Toggle.Instance.closeOpenModals();

    try {
      const partialHtml = await fetchFlyoutHtml();
      if (!partialHtml) return;

      const content = document.createRange().createContextualFragment(partialHtml).firstElementChild;
      if (!content) return;

      const existing = document.querySelector('[data-container="favourites-flyout"]');
      if (existing) {
        existing.replaceWith(content);
      } else {
        document.body.appendChild(content);
      }

      this.updateFavouriteButtonsState(content, $favouriteIds.get());

      Toggle.Instance.onToggleElement(null, target, false);
    } catch (error) {
      logCustomMessage('Error when toggling favourites flyout', {
        action: 'toggleFavouritesFlyout',
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? (error.stack ?? '') : '',
      });
    }
  };
}
